import {Injectable} from '@angular/core';
import {ArkivRessursClient, DokumentRessursClient, MøteRessursClient, OppdaterSkjermingSpesifikasjon, RestResponse, SakRessursClient, SkjermingDetaljerMedValideringsresultatModell, SkjermingDetaljerModell, SkjermingValideringsfeil} from "../ressurser/generertNg2HttpKlient";
import {firstValueFrom, Observable} from "rxjs";
import {createStore, filterNil} from "@ngneat/elf";
import {selectEntity, updateEntities, upsertEntities, withEntities} from "@ngneat/elf-entities";
import {FeilhåndteringArgumenter, MedId, opprettEllerOppdatertHendelse, serverFraLagerOgRequestPakketIMeta, serverFraRequest, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "./datatjeneste";
import {DataOgMeta, mapTilData} from "./DataOgMeta";
import {HttpResponse} from "@angular/common/http";
import {i18n, Oversettelser} from "../i18n/Oversettelser";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {initDebugger} from "../../støttefunksjoner/debug";
import {EntitetId, TypetValideringsresultatModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {tap} from "rxjs/operators";
import {caseForEnumMangler} from "felles-ng2/støttefunksjoner/enumer";
import {SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {ArkivDatatjeneste} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {SaksdokumentDatatjeneste} from "felles-ng2/tjenester/datalager/SaksdokumentDatatjeneste";
import {somSkjermingModell} from "felles-ng2/modell/skjermingModellHjelpere";

const debug = initDebugger('SkjermingDatatjeneste');

/** Datalager-spesifikk variant av {@link SkjermingDetaljerModell} som utvider denne med et
 * 'id' felt som representerer entiteten denne skjermingsdetaljer instansen er tilknyttet og
 * 'entitetType' som representerer hvilken type entitet denne instansen er tilknyttet. */
export type SkjermingDetaljerDLM = SkjermingDetaljerModell & MedId & { entitetType: SkjermetEntitetType };

/** Lager for {@link SkjermingDetaljerModell} */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class SkjermingDatatjeneste {

    /** Lager for {@link SkjermingDetaljerModell}.
     * Uten forelder, da vi ikke gjør oppslag mot noen logisk forelder */
    private skjermingsdetaljerLager = createStore(
      {name: 'skjermingsdetaljer'},
      withEntities<SkjermingDetaljerDLM>());

    constructor(private dokumentRessurs: DokumentRessursClient,
                private arkivRessurs: ArkivRessursClient,
                private sakRessurs: SakRessursClient,
                private møteRessurs: MøteRessursClient,
                private saksdokumentDatatjeneste: SaksdokumentDatatjeneste,
                private sakDatatjeneste: SakDatatjeneste,
                private møteDatatjeneste: MøteDatatjeneste,
                private arkivDatatjeneste: ArkivDatatjeneste,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser) {
        this.propagerEndringerTilAndreDatatjenester();
    }

    hentMedMeta(entitetId: EntitetId, entitetType: SkjermetEntitetType, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<SkjermingDetaljerDLM>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.hentSkjermingsdetaljer(entitetId, entitetType),
            registrerILager: (respons: HttpResponse<SkjermingDetaljerModell>) => {
                const detaljer = this.somSkjermingDetaljerDLM(respons.body!, entitetId, entitetType);
                this.skjermingsdetaljerLager.update(upsertEntities(detaljer))
                return detaljer;
            },
            hentFraLager: () => {
                return this.skjermingsdetaljerLager.pipe(selectEntity(entitetId), filterNil());
            },
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke hente skjermingsdetaljer')), 'hentMedMeta')
        });
    }

    hent(entitetId: EntitetId, entitetType: SkjermetEntitetType, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<SkjermingDetaljerDLM> {
        return this.hentMedMeta(entitetId, entitetType, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    lagre(entitetId: EntitetId, entitetType: SkjermetEntitetType, spesifikasjon: OppdaterSkjermingSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SkjermingDetaljerMedValideringsresultatDLM> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.lagreSkjermingsdetaljer(entitetId, entitetType, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const valideringsRespons = respons.body!;
                  if (!valideringsRespons.valideringsresultat.harFeil)
                      this.skjermingsdetaljerLager.update(updateEntities(entitetId, this.somSkjermingDetaljerDLM(valideringsRespons.skjermingsdetaljer, entitetId, entitetType)));
                  return valideringsRespons;
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere skjerming')), 'lagre')
        }));
    }

    //hjelpemetoder

    private hentSkjermingsdetaljer(entitetId: string, entitetType: SkjermetEntitetType): RestResponse<SkjermingDetaljerModell> {
        switch (entitetType) {
            case SkjermetEntitetType.arkivmappe:
                return this.arkivRessurs.hentSkjerming(entitetId);
            case SkjermetEntitetType.saksdokument:
            case SkjermetEntitetType.arkivdokument:
                return this.dokumentRessurs.hentSkjerming(entitetId);
            case SkjermetEntitetType.sak:
                return this.sakRessurs.hentSkjerming(entitetId);
            case SkjermetEntitetType.moete:
                return this.møteRessurs.hentSkjerming(entitetId);
            default:
                caseForEnumMangler(entitetType);
                throw new Error(`ukjent enum ${entitetType}`)
        }
    }

    private lagreSkjermingsdetaljer(entitetId: string, entitetType: SkjermetEntitetType, spesifikasjon: OppdaterSkjermingSpesifikasjon): OppdaterSkjermingsdetaljerRespons {
        switch (entitetType) {
            case SkjermetEntitetType.arkivmappe:
                return this.arkivRessurs.oppdaterSkjerming(entitetId, spesifikasjon) as OppdaterSkjermingsdetaljerRespons;
            case SkjermetEntitetType.saksdokument:
            case SkjermetEntitetType.arkivdokument:
                return this.dokumentRessurs.oppdaterSkjerming(entitetId, spesifikasjon) as OppdaterSkjermingsdetaljerRespons;
            case SkjermetEntitetType.sak:
                return this.sakRessurs.oppdaterSkjerming(entitetId, spesifikasjon) as OppdaterSkjermingsdetaljerRespons;
            case SkjermetEntitetType.moete:
                return this.møteRessurs.oppdaterSkjerming(entitetId, spesifikasjon) as OppdaterSkjermingsdetaljerRespons;
            default:
                caseForEnumMangler(entitetType);
                throw new Error(`ukjent enum ${entitetType}`)
        }
    }

    private somSkjermingDetaljerDLM(skjermingsdetaljer: SkjermingDetaljerModell, entitetId: EntitetId, entitetType: SkjermetEntitetType): SkjermingDetaljerDLM {
        return {...skjermingsdetaljer, id: entitetId, entitetType: entitetType};
    }

    /** Ved endringer i skjerming må lager som håndterer data inkludert i skjerming oppdateres */
    private propagerEndringerTilAndreDatatjenester() {
        opprettEllerOppdatertHendelse(this.skjermingsdetaljerLager).subscribe(({hendelse, entiteter}) => {
            debug(`propagerer skjermingsdetaljerLager ${hendelse.type} til møteDatatjeneste, sakDatatjeneste, saksdokumentDatatjeneste, arkivDatatjeneste for entitet-idene ${hendelse.ids}`, hendelse, entiteter);
            entiteter.forEach(skjermingDetaljer => {
                const skjerming = somSkjermingModell(skjermingDetaljer)
                switch (skjermingDetaljer.entitetType) {
                    case SkjermetEntitetType.moete:
                        this.møteDatatjeneste.oppdaterSkjermingILokallager(skjermingDetaljer.id, skjerming);
                        break;
                    case SkjermetEntitetType.sak:
                        this.sakDatatjeneste.oppdaterSkjermingILokallager(skjermingDetaljer.id, skjerming);
                        break;
                    case SkjermetEntitetType.saksdokument:
                        this.saksdokumentDatatjeneste.oppdaterSkjermingILokallager(skjermingDetaljer.id, skjerming);
                        break;
                    case SkjermetEntitetType.arkivdokument:
                        this.arkivDatatjeneste.oppdaterSkjermingAvFilILokallager(skjermingDetaljer.id, skjerming);
                        break;
                    case SkjermetEntitetType.arkivmappe:
                        this.arkivDatatjeneste.oppdaterSkjermingAvMappeILokallager(skjermingDetaljer.id, skjerming);
                        break;
                    default:
                        caseForEnumMangler(skjermingDetaljer.entitetType);
                }
            });
        });
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(feilhåndteringsstrategi: STRATEGI,
                                                                               brukerrettetFeilmelding: string,
                                                                               metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

}

/** typet versjon av {@link SkjermingDetaljerMedValideringsresultatModell} */
export type SkjermingDetaljerMedValideringsresultatDLM = SkjermingDetaljerMedValideringsresultatModell & { valideringsresultat: TypetValideringsresultatModell<SkjermingValideringsfeil>; }

type OppdaterSkjermingsdetaljerRespons = RestResponse<SkjermingDetaljerMedValideringsresultatDLM>

export enum SkjermetEntitetType {
    arkivmappe, saksdokument, arkivdokument, sak, moete
}
